import React from 'react';
import Modal, { ModalProps } from 'antd/lib/modal';
import Button from 'antd/lib/button';
import Mentions from 'antd/lib/mentions';
import {
    GeneratedReleaseNotesMarkdownContext,
    GeneratedReleaseNotesStepContext,
    PatternToken,
    TokenizedTextModalType,
} from '../types';
import { PatternTokenHelperTextComponent } from './appSettings/PatternTokenHelperTextComponent';
import { CharCounter } from './metadata/CharCounter';
import { antGrey } from './styles/globalStyles';
import Text from 'antd/lib/typography/Text';
import { GenerateReleaseNotesDropdown } from './sharedComponents/GenerateReleaseNotesDropdown';

interface TokenizedTextEditModalProps extends ModalProps {
    orgId?: string;
    appId?: string;
    releaseId?: string;
    onCancel: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
    applyChangesIsLoading: boolean;
    onSubmit: () => void;
    availableTokens: PatternToken[];
    maxLength: number;
    titleText: string;
    text: string;
    setText: React.Dispatch<React.SetStateAction<string>>;
    defaultText?: string;
    description?: string;
    modalType?: TokenizedTextModalType;
}

export const TokenizedTextEditModal: React.FC<TokenizedTextEditModalProps> = ({
    orgId,
    appId,
    releaseId,
    onCancel,
    applyChangesIsLoading,
    onSubmit,
    availableTokens,
    maxLength,
    titleText,
    text,
    description,
    setText,
    defaultText,
    modalType,
    ...rest
}) => {
    return (
        <>
            <Modal
                width={modalType === TokenizedTextModalType.ReleaseSummaryMessage ? '700px' : '600px'}
                title={titleText}
                {...rest}
                onCancel={onCancel}
                bodyStyle={{
                    overflow: 'auto',
                    height: 600,
                }}
                footer={
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button onClick={onCancel}>Cancel</Button>
                        {defaultText && (
                            <Button
                                onClick={() => {
                                    setText(defaultText);
                                }}
                            >
                                Restore default message
                            </Button>
                        )}
                        <Button loading={applyChangesIsLoading} type="primary" onClick={onSubmit}>
                            Apply
                        </Button>
                    </div>
                }
            >
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 16,
                        paddingBottom: 16,
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 8,
                            alignItems: 'flex-end',
                        }}
                    >
                        {modalType === TokenizedTextModalType.ReleaseSummaryMessage && orgId && appId && releaseId ?
                            <GenerateReleaseNotesDropdown
                                orgId={orgId}
                                appId={appId}
                                releaseId={releaseId}
                                stepContext={GeneratedReleaseNotesStepContext.Release}
                                markdownContext={GeneratedReleaseNotesMarkdownContext.MarkdownUnsupported}
                            />
                        :   null}
                        <Mentions
                            prefix={['{']}
                            split=""
                            options={availableTokens.map((token) => {
                                return {
                                    label: token,
                                    value: token.slice(1, token.length),
                                };
                            })}
                            rows={8}
                            value={text}
                            onChange={setText}
                            maxLength={maxLength}
                        />
                        <div
                            style={{
                                padding: '0 8px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                width: '100%',
                                gap: 24,
                            }}
                        >
                            <div
                                style={{
                                    flex: 1,
                                }}
                            >
                                {description && (
                                    <Text style={{ color: antGrey.grey7, fontSize: 12 }}>{description}</Text>
                                )}
                            </div>
                            {maxLength && <CharCounter maxLength={maxLength} stringValue={text} />}
                        </div>
                    </div>
                </div>

                {availableTokens.length > 0 && (
                    <div
                        style={{
                            backgroundColor: antGrey.grey2,
                        }}
                    >
                        <PatternTokenHelperTextComponent availableTokens={availableTokens} />
                    </div>
                )}
            </Modal>
        </>
    );
};
